import React from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useWatch } from 'react-hook-form'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material'
import CloudIcon from '@mui/icons-material/Cloud'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import EmailInformationFields from './EmailInformationFields'
import ReceiverInformationFields from './ReceiverInformationFields'

const ExternalServiceSection = ({
  control,
  errors,
  isPasswordShown,
  onTogglePassword
}) => {
  const { t } = useTranslation()
  const emailUserName = useWatch({ control, name: 'emailUserName' })
  const emailDomain = emailUserName?.split('@')[1] || ''

  return (
    <Box sx={{ mt: 3 }}>
      <Card variant="outlined" sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 10, textAlign: 'center' }}>
            <CloudIcon sx={{
              fontSize: 48,
              color: 'primary.main',
              mb: 2,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}/>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('mailTemplate.users.modal.externalServiceTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              {t('mailTemplate.users.modal.externalServiceInfo')}
            </Typography>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={4}>
              <EmailInformationFields
                control={control}
                errors={errors}
                isPasswordShown={isPasswordShown}
                onTogglePassword={onTogglePassword}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" sx={{ mb: 2.5, color: 'text.primary' }}>
                  {t('mailTemplate.users.modal.domainInformation')}
                </Typography>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      label={t('mailTemplate.users.modal.domain')}
                      value={emailDomain}
                      placeholder={t('mailTemplate.users.modal.domainP')}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={t('mailTemplate.users.modal.domainAutoFilled')} arrow>
                              <LockOutlinedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="mailFromDomain"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          variant="outlined"
                          size="small"
                          label={t('mailTemplate.users.modal.mailFromDomain')}
                          placeholder={t('mailTemplate.users.modal.mailFromDomainP')}
                          InputProps={{
                            endAdornment: emailDomain
                              ? (
                                <InputAdornment position="end" sx={{ maxWidth: '50%' }}>
                                  <Typography
                                    variant="body2"
                                    title={`.${emailDomain}`}
                                    sx={{
                                      color: 'text.secondary',
                                      fontWeight: 500,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      borderLeft: '1px solid',
                                      borderColor: 'divider',
                                      pl: 1,
                                      ml: 1.5
                                    }}
                                  >
                                    .{emailDomain}
                                  </Typography>
                                </InputAdornment>
                                )
                              : null
                          }}
                          {...(errors.mailFromDomain && {
                            error: true,
                            helperText: t('mailTemplate.users.modal.errors.mailFromDomain')
                          })}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <ReceiverInformationFields
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  )
}

export default ExternalServiceSection
